import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import { ProviderSyncIssueDetail, useSyncIssueDetail } from "../../api/syncissues";

export default function SyncIssueDetails() {
    const { id } = useParams<{ id: string }>();
    const issueId = id ? parseInt(id, 10) : undefined;

    return (
        <DashboardLayout>
            <ProviderSyncIssueDetail issueId={Number.isFinite(issueId) ? issueId : undefined}>
                <SyncIssueDetailsContent />
            </ProviderSyncIssueDetail>
        </DashboardLayout>
    );
}

function SyncIssueDetailsContent() {
    const navigate = useNavigate();
    const { syncIssue, errors, loading, deleting, fetchSyncIssue, deleteSyncIssue } = useSyncIssueDetail();
    const [deleteOpen, setDeleteOpen] = useState(false);

    const hasData = syncIssue ? Object.keys(syncIssue.data).length > 0 : false;

    const handleConfirmDelete = async () => {
        const success = await deleteSyncIssue();
        if (success) {
            setDeleteOpen(false);
            navigate("/sync-issues");
        }
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackRoundedIcon />}
                    onClick={() => navigate("/sync-issues")}
                >
                    Back
                </Button>
                {syncIssue ? (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteRoundedIcon />}
                        onClick={() => setDeleteOpen(true)}
                        disabled={deleting}
                    >
                        Delete
                    </Button>
                ) : null}
            </Stack>

            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchSyncIssue}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}

            {loading ? (
                <_DetailsSkeleton />
            ) : syncIssue ? (
                <StaggerContainer>
                    <StaggerItem>
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary">
                                    Type
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {syncIssue.type}
                                </Typography>
                                <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                                    User
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {syncIssue.user.name}
                                </Typography>
                                {syncIssue.user.email ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {syncIssue.user.email}
                                    </Typography>
                                ) : null}
                            </CardContent>
                        </Card>
                    </StaggerItem>

                    <StaggerItem>
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                                    Content
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                >
                                    {syncIssue.content || "—"}
                                </Typography>
                            </CardContent>
                        </Card>
                    </StaggerItem>

                    <StaggerItem>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            Data
                        </Typography>
                        <Card variant="outlined">
                            <CardContent>
                                {!hasData ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                                        No data fields for this issue.
                                    </Typography>
                                ) : (
                                    <_SyncIssueDataTable data={syncIssue.data} />
                                )}
                            </CardContent>
                        </Card>
                    </StaggerItem>
                </StaggerContainer>
            ) : null}

            <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
                <DialogTitle>Delete sync issue</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this sync issue? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                        onClick={handleConfirmDelete}
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function _formatPrimitive(value: unknown): string {
    if (value == null) {
        return "—";
    }
    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }
    return String(value);
}

function _SyncIssueDataTable({ data }: { data: Record<string, unknown> }) {
    const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: "30%" }}>Key</TableCell>
                    <TableCell>Value</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {entries.map(([key, value]) => (
                    <TableRow key={key}>
                        <TableCell sx={{ fontWeight: 500, verticalAlign: "top" }}>
                            {key}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                            <_DataValue value={value} />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function _DataValue({ value }: { value: unknown }) {
    if (value == null) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        );
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    []
                </Typography>
            );
        }

        return (
            <Stack spacing={2}>
                {value.map((item, index) => (
                    <Box key={index}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            [{index}]
                        </Typography>
                        {item != null && typeof item === "object" ? (
                            <Box sx={{ pl: 1, borderLeft: 2, borderColor: "divider" }}>
                                <_SyncIssueDataTable data={item as Record<string, unknown>} />
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {_formatPrimitive(item)}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Stack>
        );
    }

    if (typeof value === "object") {
        return (
            <Box sx={{ pl: 1, borderLeft: 2, borderColor: "divider" }}>
                <_SyncIssueDataTable data={value as Record<string, unknown>} />
            </Box>
        );
    }

    return (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {_formatPrimitive(value)}
        </Typography>
    );
}

function _DetailsSkeleton() {
    return (
        <Stack spacing={3}>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={60} />
                    <Skeleton variant="text" width="40%" />
                </CardContent>
            </Card>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={80} />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="80%" />
                </CardContent>
            </Card>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={60} />
                    <Skeleton variant="rectangular" height={120} />
                </CardContent>
            </Card>
        </Stack>
    );
}
